import type { MeQuery } from '#graphql-operations'

export type Influencer = NonNullable<NonNullable<MeQuery['me']>['influencer']>

type FetchStatus = 'idle' | 'pending' | 'success' | 'error'

/** Forget the cached profile: its customer is no longer the one signed in. */
export function clearInfluencer() {
  useState<Influencer | null>('affiliate:influencer').value = null
  useState<FetchStatus>('affiliate:influencer:status').value = 'idle'
  useState<string | null>('affiliate:influencer:customer').value = null
}

/**
 * App-scoped affiliate state.
 *
 * The `influencer` object hangs off `CurrentUser` (Vendure affiliates plugin) and
 * is only reachable through the `me` query — not the shared `activeCustomer` fetch
 * in `layers/common`. Rather than couple that shared auth plugin to an
 * exalted-only operation, we resolve the influencer here, on the client, once per
 * customer and cache it in `useState` so both the account tab bar and the portal
 * page read from a single source.
 *
 * The cache belongs to the customer it was resolved for. Signing out, or in as
 * someone else, makes it read as empty at once — before any refetch — so the
 * next person never sees the previous one's portal.
 */
export function useInfluencer() {
  const auth = useAuth()
  const cached = useState<Influencer | null>('affiliate:influencer', () => null)
  const cachedStatus = useState<FetchStatus>('affiliate:influencer:status', () => 'idle')
  const owner = useState<string | null>('affiliate:influencer:customer', () => null)

  const customerId = computed(() => {
    const id = auth?.activeCustomer.value?.id
    return id != null ? String(id) : null
  })
  const isOwn = computed(() => customerId.value != null && owner.value === customerId.value)

  const influencer = computed(() => (isOwn.value ? cached.value : null))
  const status = computed<FetchStatus>(() => (isOwn.value ? cachedStatus.value : 'idle'))
  const isInfluencer = computed(() => influencer.value != null)

  async function fetchInfluencer(force = false) {
    const id = customerId.value
    // Signed out: drop the previous customer's profile rather than keep it hidden.
    if (!id) {
      clearInfluencer()
      return null
    }

    // De-dupe concurrent callers (tab bar + page mount) and avoid refetching once
    // resolved for this customer unless explicitly forced.
    if (owner.value === id && cachedStatus.value === 'pending')
      return cached.value
    if (owner.value === id && cachedStatus.value === 'success' && !force)
      return cached.value

    if (owner.value !== id)
      cached.value = null
    owner.value = id
    cachedStatus.value = 'pending'
    try {
      const { data } = await useGraphqlQuery('me')
      // The customer changed while this was in flight: the answer is someone else's.
      if (owner.value !== id)
        return influencer.value
      cached.value = data.me?.influencer ?? null
      cachedStatus.value = 'success'
    }
    catch (err) {
      console.error('[affiliate] failed to resolve influencer', err)
      if (owner.value === id)
        cachedStatus.value = 'error'
    }
    return influencer.value
  }

  return { influencer, isInfluencer, status, fetchInfluencer }
}
